/**
 * 🟥 CUADRO NEGRO P1 — ErrorBoundary
 *
 * Error Boundary genérico para capturar fallos de render en cualquier
 * subárbol React. Uso principal: envolver MermaidDiagram para que un
 * "Syntax error in text" de mermaid v11.x sobre un mensaje antiguo no
 * rompa el MessageBubble entero ni el scroll del chat.
 *
 * Cuando se captura un error:
 *  - se muestra un placeholder discreto (no estridente),
 *  - se loguea en consola para diagnóstico,
 *  - el resto del mensaje (texto + otros bloques de código) sigue funcionando.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: (error && error.message) ? String(error.message).slice(0, 200) : '',
    };
  }

  componentDidCatch(error, info) {
    // No spam — solo un log informativo. El bug real ya quedó capturado.
    console.error('[ErrorBoundary] caught render error:', error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      const fallback = this.props.fallback;
      if (typeof fallback === 'function') {
        return fallback(this.state.errorMessage);
      }
      if (fallback) return fallback;
      return (
        <div
          className="my-2 px-3 py-2 rounded-md border border-rose-500/30 bg-rose-950/20 text-xs text-rose-300 light:bg-rose-50 light:border-rose-200 light:text-rose-700 flex items-start gap-2"
          data-testid="error-boundary-fallback"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">No se pudo renderizar este contenido.</div>
            {this.state.errorMessage && (
              <div className="opacity-70 mt-0.5 font-mono break-all">
                {this.state.errorMessage}
              </div>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
